// BARKODSUZ GÖNDERİ EŞLEŞTİRME (SAF, ağ/DB yok).
//
// Bazı cariler gönderilerini kargo firmasına Excel ile ve bizim sipariş barkodumuz OLMADAN yükler.
// `musteribarkod` ya boş kalır ya da kargo firmasının ürettiği bir değerle dolar (TEKNOAVM: Aras
// "269000000080", PTT takip no "2754846790127"). Her iki durumda da gönderi /tracking'de bizim
// barkodumuzla bulunamaz, `cargo_shipments` siparişe bağlanamaz ve sipariş kargo durumu güncellenmez.
// Bu modül `/cargo` satırındaki alıcı bilgisini sipariş adaylarıyla karşılaştırır.
//
// Kural (kullanıcı kararı, 2026-10-09; API ile gönderilmiş 1.501 bağlı gönderide doğrulandı: telefon
// %100, ad-soyad %99,9, il %100, ilçe %99,3 tutuyor):
//  - Telefon + ad-soyad ZORUNLU. Kısmi ad (göbek adı eksik vb.) ancak il de tutarsa kabul edilir.
//  - Tutar sert koşul DEĞİL: cari tutarı genelde yukarı yuvarlar (2.499 → 2.500). Yalnız aynı müşterinin
//    birden çok siparişi arasında ayırıcıdır; il/ilçe/sipno da öyle.
//  - Tarih yalnız geniş bir pencere: carinin kabul tarihi siparişle tam tutmayabilir.
//  - Tek aday kalmazsa ya da iki gönderi aynı siparişi gösterirse BAĞLANMAZ — yanlış bağlama (müşterinin
//    başka siparişinin durumunu değiştirmek), bağlamamaktan pahalıdır. [[opop-deposel-excel-reconcile]]

import { stripLeadingZeros } from './barcode'

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Eşleştirilecek barkodsuz gönderi (cargo_shipments satırı + ham /cargo alıcı alanları). */
export interface BarcodelessShipment {
  id: string
  phone: string | null
  /** Alıcı ad + soyad (aliciadi + alicisoyad). */
  name: string | null
  il: string | null
  ilce: string | null
  /** Tahsilat tutarı (TL); 0 = kapıda tahsilat yok (online/havale). */
  amount: number | null
  /** Kabul tarihi, YYYY-MM-DD. */
  alimTarihi: string | null
  sipno: string | null
}

/** Sipariş adayı. */
export interface MatchCandidate {
  id: string
  phone: string | null
  fullName: string | null
  city: string | null
  district: string | null
  /** Kargo firmasına gidecek tahsilat tutarı (ürün + kargo); bilinmiyorsa null. */
  expectedAmount: number | null
  orderNumber: string | null
  /** Sipariş oluşturma zamanı (ISO). */
  createdAt: string
}

export type NameMatch = 'exact' | 'partial' | 'none'
export type AmountMatch = 'exact' | 'rounded' | 'mismatch' | 'unknown'

export interface MatchSignals {
  name: Exclude<NameMatch, 'none'>
  il: boolean
  ilce: boolean
  amount: AmountMatch
  sipno: boolean
  score: number
}

export type ShipmentMatch =
  | { status: 'matched'; orderId: string; signals: MatchSignals }
  /** Birden çok aday aynı puanda. */
  | { status: 'ambiguous'; orderIds: string[] }
  /** Bu gönderinin seçtiği siparişi başka bir gönderi de seçti (bölünmüş paket / yeniden gönderim). */
  | { status: 'conflict'; orderId: string }
  /** `phoneOnly`: telefonu tutan ama adı tutmayan adaylar (rapor için). */
  | { status: 'none'; reason: 'no_phone' | 'no_candidate' | 'name_mismatch'; phoneOnly?: string[] }

export interface MatchOptions {
  /** Sipariş, kabulden en çok bu kadar gün önce oluşturulmuş olabilir. */
  maxDaysBeforeAlim: number
  /** Kabul tarihi kaydı tam tutmayabilir: sipariş kabulden bu kadar gün SONRA da oluşmuş görünebilir. */
  maxDaysAfterAlim: number
  /** Yukarı yuvarlama toleransı (TL): 0 < tutar − beklenen ≤ bu değer → "rounded". */
  roundingTolerance: number
}

export const DEFAULT_MATCH_OPTIONS: MatchOptions = {
  maxDaysBeforeAlim: 45,
  maxDaysAfterAlim: 3,
  roundingTolerance: 50,
}

const DAY_MS = 86_400_000

/**
 * Gönderideki barkod siparişin barkoduyla aynı mı (baştaki sıfırlar yok sayılır)? Aynıysa gönderi
 * /tracking'de sipariş barkoduyla bulunur; değilse (barkodsuz eşleşme) durum saklanan liste satırından
 * alınmalıdır.
 */
export function shipmentBarcodeMatchesOrder(shipmentBarcode: string | null | undefined, orderBarcode: string | null | undefined): boolean {
  const s = shipmentBarcode?.trim()
  const o = orderBarcode?.trim()
  return !!s && !!o && stripLeadingZeros(s) === stripLeadingZeros(o)
}

/** Telefonu karşılaştırma anahtarına çevirir: yalnız rakamlar, son 10 hane (0/90 öneki atılır). */
export function phoneKey(v: string | null | undefined): string | null {
  const digits = String(v ?? '').replace(/\D/g, '')
  return digits.length >= 10 ? digits.slice(-10) : null
}

/** Türkçe katlama + yalnız harf/rakam belirteçleri ("ŞÜKRÜ  Öztürk" → ["sukru", "ozturk"]). */
export function nameTokens(v: string | null | undefined): string[] {
  return String(v ?? '')
    .replace(/I/g, 'ı').replace(/İ/g, 'i')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // birleşik nokta/şapka (i̇, â)
    .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

function foldPlace(v: string | null | undefined): string {
  return nameTokens(v).join('')
}

/** Soyad yerine yazılan hitaplar: müşteri yalnız adını verince FEST'e "MURAT BEY" / "YEŞİM HANIM" girilir. */
const HONORIFICS = new Set(['bey', 'hanim', 'bay', 'bayan'])

/** Karşılaştırma belirteçleri: hitaplar atılır (yalnız hitap kalıyorsa olduğu gibi bırakılır). */
function personTokens(v: string | null | undefined): string[] {
  const tokens = nameTokens(v)
  const stripped = tokens.filter((t) => !HONORIFICS.has(t))
  return stripped.length ? stripped : tokens
}

/** Aynı ya da tek harf farklı (ekleme/silme/değiştirme) — yalnız 5+ harfli belirteçlerde yazım hatası toleransı. */
export function nearToken(a: string, b: string): boolean {
  if (a === b) return true
  if (Math.min(a.length, b.length) < 5 || Math.abs(a.length - b.length) > 1) return false
  let i = 0
  let j = 0
  let edits = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { i++; j++; continue }
    if (++edits > 1) return false
    if (a.length > b.length) i++
    else if (a.length < b.length) j++
    else { i++; j++ }
  }
  return edits + (a.length - i) + (b.length - j) <= 1
}

/**
 * Ad karşılaştırması (hitaplar atılarak):
 *  - "exact": belirteç kümeleri aynı (sıra/boşluk/büyük-küçük harf farkı önemsiz; "MURAT BEY" = "Murat").
 *  - "partial": tek harflik yazım hatası ("KARTEPE" ~ "Karatepe"), göbek adı eksik ya da soyad + en az bir
 *    ad daha tutuyor. Kısmi eşleşme çağıranda ancak il de tutarsa kabul edilir.
 * Yalnız soyadı tutan farklı kişi ("Mustafa Aydemir" / "Mehmet Aydemir") "none"dur.
 */
export function compareNames(a: string | null | undefined, b: string | null | undefined): NameMatch {
  const ta = personTokens(a)
  const tb = personTokens(b)
  if (ta.length === 0 || tb.length === 0) return 'none'
  const sa = new Set(ta)
  const sb = new Set(tb)
  if (sa.size === sb.size && [...sa].every((t) => sb.has(t))) return 'exact'
  const [small, big] = sa.size <= sb.size ? [[...sa], [...sb]] : [[...sb], [...sa]]
  const shared = small.filter((t) => big.some((u) => nearToken(t, u))).length
  // Tek belirteçli (yalnız ad/soyad) isimler kısmi sayılmaz: çok genel.
  if (small.length >= 2 && shared === small.length) return 'partial'
  if (shared >= 2 && nearToken(ta[ta.length - 1], tb[tb.length - 1])) return 'partial'
  return 'none'
}

/** Gönderi tutarını beklenenle karşılaştırır (cari yukarı yuvarlar: 2.499 → 2.500). */
export function compareAmount(
  shipmentAmount: number | null,
  expected: number | null,
  tolerance = DEFAULT_MATCH_OPTIONS.roundingTolerance,
): AmountMatch {
  // 0 tutar = kapıda tahsilat yok (online/havale) — beklenenle karşılaştırılamaz.
  if (shipmentAmount == null || expected == null || shipmentAmount === 0) return 'unknown'
  const diff = Math.round((shipmentAmount - expected) * 100) / 100
  if (Math.abs(diff) < 0.01) return 'exact'
  if (diff > 0 && diff <= tolerance) return 'rounded'
  return 'mismatch'
}

function istanbulDay(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(iso))
}

/** Sipariş oluşturma günü kabul tarihine göre pencerede mi? Kabul tarihi yoksa pencere uygulanmaz. */
function inDateWindow(alimTarihi: string | null, createdAt: string, opts: MatchOptions): boolean {
  if (!alimTarihi) return true
  const alim = Date.parse(`${alimTarihi}T12:00:00+03:00`)
  const created = Date.parse(`${istanbulDay(createdAt)}T12:00:00+03:00`)
  const daysBefore = (alim - created) / DAY_MS
  return daysBefore <= opts.maxDaysBeforeAlim && -daysBefore <= opts.maxDaysAfterAlim
}

const AMOUNT_SCORE: Record<AmountMatch, number> = { exact: 3, rounded: 3, unknown: 1, mismatch: 0 }

/** Tek gönderiyi adaylarla karşılaştırır (diğer gönderilerden bağımsız). */
export function matchShipment(
  shipment: BarcodelessShipment,
  candidates: readonly MatchCandidate[],
  opts: MatchOptions = DEFAULT_MATCH_OPTIONS,
): ShipmentMatch {
  const phone = phoneKey(shipment.phone)
  if (!phone) return { status: 'none', reason: 'no_phone' }
  const sameName: { id: string; signals: MatchSignals }[] = []
  const phoneOnly: string[] = []
  for (const c of candidates) {
    if (phoneKey(c.phone) !== phone) continue
    if (!inDateWindow(shipment.alimTarihi, c.createdAt, opts)) continue
    const name = compareNames(shipment.name, c.fullName)
    const il = !!shipment.il && foldPlace(shipment.il) === foldPlace(c.city)
    if (name === 'none' || (name === 'partial' && !il)) {
      phoneOnly.push(c.id)
      continue
    }
    const ilce = !!shipment.ilce && foldPlace(shipment.ilce) === foldPlace(c.district)
    const amount = compareAmount(shipment.amount, c.expectedAmount, opts.roundingTolerance)
    const sipno = !!shipment.sipno && !!c.orderNumber && shipment.sipno.trim() === c.orderNumber.trim()
    const score = (name === 'exact' ? 4 : 2) + (il ? 1 : 0) + (ilce ? 1 : 0) + AMOUNT_SCORE[amount] + (sipno ? 3 : 0)
    sameName.push({ id: c.id, signals: { name, il, ilce, amount, sipno, score } })
  }
  if (sameName.length === 0) {
    return phoneOnly.length ? { status: 'none', reason: 'name_mismatch', phoneOnly } : { status: 'none', reason: 'no_candidate' }
  }
  sameName.sort((a, b) => b.signals.score - a.signals.score)
  const top = sameName[0]
  const tied = sameName.filter((x) => x.signals.score === top.signals.score)
  if (tied.length > 1) return { status: 'ambiguous', orderIds: tied.map((x) => x.id) }
  return { status: 'matched', orderId: top.id, signals: top.signals }
}

/**
 * Gönderileri toplu eşleştirir. Aynı siparişi birden çok gönderi seçerse hiçbiri bağlanmaz ("conflict"):
 * bölünmüş paket ya da yeniden gönderim olabilir, hangisinin asıl olduğu elle karar gerektirir.
 */
export function matchBarcodelessShipments(
  shipments: readonly BarcodelessShipment[],
  candidates: readonly MatchCandidate[],
  opts: MatchOptions = DEFAULT_MATCH_OPTIONS,
): Map<string, ShipmentMatch> {
  const result = new Map<string, ShipmentMatch>()
  const claims = new Map<string, string[]>()
  for (const s of shipments) {
    const m = matchShipment(s, candidates, opts)
    result.set(s.id, m)
    if (m.status === 'matched') claims.set(m.orderId, [...(claims.get(m.orderId) ?? []), s.id])
  }
  for (const [orderId, shipmentIds] of claims) {
    if (shipmentIds.length < 2) continue
    for (const id of shipmentIds) result.set(id, { status: 'conflict', orderId })
  }
  return result
}

/** Ham `/cargo` satırından eşleştirme girdisini çıkarır (alıcı alanları upsert'te ayrıca saklanmaz). */
export function barcodelessShipmentFromRaw(
  id: string,
  raw: any,
  stored: { il: string | null; ilce: string | null; amount: number | null; alim_tarihi: string | null; sipno: string | null },
): BarcodelessShipment {
  const name = [raw?.aliciadi, raw?.alicisoyad].map((x) => String(x ?? '').trim()).filter(Boolean).join(' ')
  return {
    id,
    phone: raw?.telno != null ? String(raw.telno) : null,
    name: name || null,
    il: stored.il,
    ilce: stored.ilce,
    amount: stored.amount,
    alimTarihi: stored.alim_tarihi,
    sipno: stored.sipno,
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
